import {
  Coordinates,
  CalculationMethod,
  PrayerTimes,
  Madhab,
  SunnahTimes,
  type CalculationParameters,
} from "adhan";

export type MethodId =
  | "Karachi"
  | "MuslimWorldLeague"
  | "Egyptian"
  | "UmmAlQura"
  | "NorthAmerica"
  | "Dubai"
  | "Qatar"
  | "Singapore"
  | "Turkey";

export type MadhabId = "hanafi" | "standard";

export const METHODS: { id: MethodId; en: string; bn: string }[] = [
  { id: "Karachi", en: "Karachi (University of Islamic Sciences)", bn: "করাচী (BD ডিফল্ট)" },
  { id: "MuslimWorldLeague", en: "Muslim World League", bn: "মুসলিম ওয়ার্ল্ড লীগ" },
  { id: "Egyptian", en: "Egyptian General Authority", bn: "মিসরীয় কর্তৃপক্ষ" },
  { id: "UmmAlQura", en: "Umm Al-Qura, Makkah", bn: "উম্মুল কুরা, মক্কা" },
  { id: "NorthAmerica", en: "ISNA (North America)", bn: "ISNA (উত্তর আমেরিকা)" },
  { id: "Dubai", en: "Dubai", bn: "দুবাই" },
  { id: "Qatar", en: "Qatar", bn: "কাতার" },
  { id: "Singapore", en: "Singapore", bn: "সিঙ্গাপুর" },
  { id: "Turkey", en: "Diyanet (Turkey)", bn: "দিয়ানেত (তুরস্ক)" },
];

function buildParams(method: MethodId): CalculationParameters {
  switch (method) {
    case "Karachi": return CalculationMethod.Karachi();
    case "MuslimWorldLeague": return CalculationMethod.MuslimWorldLeague();
    case "Egyptian": return CalculationMethod.Egyptian();
    case "UmmAlQura": return CalculationMethod.UmmAlQura();
    case "NorthAmerica": return CalculationMethod.NorthAmerica();
    case "Dubai": return CalculationMethod.Dubai();
    case "Qatar": return CalculationMethod.Qatar();
    case "Singapore": return CalculationMethod.Singapore();
    case "Turkey": return CalculationMethod.Turkey();
  }
}

export interface DailyTimes {
  fajr: Date;
  sunrise: Date;
  dhuhr: Date;
  asr: Date;
  maghrib: Date;
  isha: Date;
  midnight: Date;
}

export function computePrayerTimes(
  lat: number,
  lon: number,
  date: Date,
  method: MethodId = "Karachi",
  madhab: MadhabId = "hanafi",
): DailyTimes {
  const coords = new Coordinates(lat, lon);
  const params = buildParams(method);
  params.madhab = madhab === "hanafi" ? Madhab.Hanafi : Madhab.Shafi;
  const pt = new PrayerTimes(coords, date, params);
  const sunnah = new SunnahTimes(pt);
  return {
    fajr: pt.fajr,
    sunrise: pt.sunrise,
    dhuhr: pt.dhuhr,
    asr: pt.asr,
    maghrib: pt.maghrib,
    isha: pt.isha,
    midnight: sunnah.middleOfTheNight,
  };
}

export type PrayerKey = "fajr" | "sunrise" | "dhuhr" | "asr" | "maghrib" | "isha";

export const PRAYER_LABELS: Record<PrayerKey, { en: string; bn: string }> = {
  fajr: { en: "Fajr", bn: "ফজর" },
  sunrise: { en: "Sunrise", bn: "সূর্যোদয়" },
  dhuhr: { en: "Dhuhr", bn: "যোহর" },
  asr: { en: "Asr", bn: "আসর" },
  maghrib: { en: "Maghrib", bn: "মাগরিব" },
  isha: { en: "Isha", bn: "এশা" },
};

export function currentAndNext(times: DailyTimes, now: Date): { current: PrayerKey | null; next: PrayerKey | null; nextTime: Date } {
  const order: PrayerKey[] = ["fajr", "sunrise", "dhuhr", "asr", "maghrib", "isha"];
  const ts = order.map((k) => times[k].getTime());
  const t = now.getTime();
  let current: PrayerKey | null = null;
  let next: PrayerKey | null = null;
  for (let i = 0; i < order.length; i++) {
    if (t >= ts[i]) current = order[i];
    if (t < ts[i] && next === null) next = order[i];
  }
  // tomorrow's fajr if past isha
  let nextTime: Date;
  if (next) {
    nextTime = times[next];
  } else {
    nextTime = new Date(times.fajr.getTime() + 24 * 3600 * 1000);
    next = "fajr";
  }
  return { current, next, nextTime };
}

export function formatTime(d: Date, mode: "12" | "24" = "12"): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const mm = m.toString().padStart(2, "0");
  if (mode === "24") return `${h.toString().padStart(2, "0")}:${mm}`;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h12}:${mm} ${ampm}`;
}

/** Approximate Hijri (Umm al-Qura) using Intl. */
export function hijriString(d: Date, lang: "en" | "bn" = "en"): string {
  try {
    const fmt = new Intl.DateTimeFormat(lang === "bn" ? "bn-u-ca-islamic-umalqura" : "en-u-ca-islamic-umalqura", {
      day: "numeric", month: "long", year: "numeric",
    });
    return fmt.format(d).replace(/\s?AH$/i, "").trim() + (lang === "en" ? " AH" : " হিজরি");
  } catch {
    return "";
  }
}
